// ─────────────────────────────────────────────────────────────────────────────
// Ce que l'API accepte de MOI — chargé depuis l'API, jamais recopié.
//
// ── Le défaut qu'on supprime ────────────────────────────────────────────────
//
// `canOpen` répondait « cet écran est-il à moi ? ». Il ne répondait pas « ai-je
// le droit d'y écrire ? ». Or core-api pose DEUX gardes indépendants : un
// module ET un rôle de tenant. Un `viewer` à qui le module `audits` est accordé
// voyait donc l'écran complet, bouton « Lancer un audit profond » actif, et
// recevait 403 en cliquant — constaté sur staging le 15 septembre 2026.
//
// ── Pourquoi CHARGER la table plutôt que l'écrire ici ───────────────────────
//
// Soixante-treize routes exigent plus que `viewer`. Une table recopiée dans le
// navigateur diverge au premier durcissement : la route change côté API, le
// bouton reste actif ici, et l'écart ne se voit qu'au 403 d'un utilisateur —
// trop tard, et chez quelqu'un d'autre.
//
// `GET /v1/capabilities` rend ce qui est RELEVÉ à l'enregistrement des routes,
// sur les gardes eux-mêmes. Une route ajoutée demain y figure sans que
// personne n'y pense, des deux côtés.
//
// ── Ce que ce n'est pas ─────────────────────────────────────────────────────
//
// Pas une sécurité. Les claims sont lus sans vérifier la signature, et cette
// table n'est qu'une description. L'autorité reste `requireTenantAccess` et
// `requireModuleAccess` côté API. Ici on s'engage seulement à ne pas PROPOSER
// ce qui sera refusé.
// ─────────────────────────────────────────────────────────────────────────────
import { computed, ref } from 'vue';
import { firstValueFrom } from 'rxjs';
import { API_BASE_URL, authHeaders$, fetchJson$ } from '@/api/httpClient';
import { i18n } from '@/i18n';
import { useSession } from './session';

export interface RouteRequirement {
  /** `null` quand la route n'exige rien de plus que d'être authentifié. */
  role: string | null;
  /** Une permission, plusieurs (l'une suffit), ou aucune. */
  module: string | string[] | null;
}

const exigences = ref<Record<string, RouteRequirement>>({});
const charge = ref(false);
let enCours: Promise<void> | null = null;

/**
 * Charge la table UNE fois par session.
 *
 * Idempotent et partagé : dix écrans montés en même temps ne déclenchent qu'un
 * appel. Un échec n'est pas rejoué en boucle — il laisse la table vide, et
 * `can()` retombe alors sur le rôle seul (voir plus bas).
 */
export async function loadCapabilities(): Promise<void> {
  if (charge.value) return;
  if (enCours !== null) return enCours;

  enCours = (async () => {
    try {
      const headers = await firstValueFrom(authHeaders$());
      const res = await firstValueFrom(
        fetchJson$<{ data: { routes: Record<string, RouteRequirement> } }>(
          `${API_BASE_URL}/v1/capabilities`,
          { headers },
        ),
      );
      exigences.value = res.data.routes;
    } catch {
      // Table vide : `can()` répond alors sur le seul rôle porté par le jeton.
      // Ne PAS ouvrir tout en grand, ne pas tout fermer non plus — l'API
      // tranchera de toute façon.
      exigences.value = {};
    } finally {
      charge.value = true;
      enCours = null;
    }
  })();

  return enCours;
}

/**
 * La relire pour le compte qui vient de se connecter (2 octobre 2026).
 *
 * `main.ts` ne la charge qu'au démarrage, quand une session est déjà ouverte.
 * Après une connexion sans rechargement, la table restait vide et `can()`
 * laissait tout passer jusqu'au rechargement suivant : le Sonar seul demandait
 * les formats de Projector, que l'API refusait en 403. Un autre compte connecté
 * dans le même onglet ne garde pas non plus la table du précédent.
 */
export async function rechargerCapabilities(): Promise<void> {
  if (enCours !== null) await enCours;
  exigences.value = {};
  charge.value = false;
  return loadCapabilities();
}

export function useCapabilities() {
  const { canWrite, canOpen, isSuperadmin, role } = useSession();

  /**
   * Puis-je appeler cette route ?
   *
   * La clé est celle de l'API : `'POST /v1/brands/:id/audits'`, avec les
   * PARAMÈTRES tels qu'ils sont déclarés, pas l'URL concrète. C'est la même
   * chaîne des deux côtés — une faute de frappe rend donc `true` (route
   * inconnue = rien d'exigé), ce qui laisse l'API refuser plutôt que de
   * griser un bouton légitime. Le test `capabilities.spec` vérifie que toutes
   * les clés employées par le front existent bien dans la table servie.
   */
  const can = (cle: string): boolean => {
    const exig = exigences.value[cle];
    // Route absente de la table : elle n'exige rien de plus qu'un jeton (le
    // serveur omet délibérément ces lignes), ou la table n'a pas pu être
    // chargée. Dans les deux cas on n'interdit pas.
    if (exig === undefined) return true;
    if (isSuperadmin.value) return true;

    if (exig.role !== null && !canWrite(exig.role as 'analyst' | 'admin' | 'owner')) return false;

    if (exig.module !== null) {
      const modules = Array.isArray(exig.module) ? exig.module : [exig.module];
      if (!modules.some((m) => canOpen(m))) return false;
    }
    return true;
  };

  /** L'infobulle qui REMPLACE celle du bouton, et qui NOMME ce qui manque, dans la langue du lecteur. */
  const pourquoiPas = (cle: string): string => {
    const exig = exigences.value[cle];
    if (exig === undefined) return '';
    const { t } = i18n.global;

    if (exig.role !== null && !canWrite(exig.role as 'analyst' | 'admin' | 'owner')) {
      return t('commun.droits.role', { role: role.value ?? t('commun.droits.roleInconnu'), requis: exig.role });
    }
    if (exig.module !== null) {
      const modules = Array.isArray(exig.module) ? exig.module : [exig.module];
      if (!modules.some((m) => canOpen(m))) {
        return t('commun.droits.module', { modules: modules.join(` ${t('commun.droits.ou')} `) });
      }
    }
    return '';
  };

  /**
   * L'infobulle d'un bouton, en un seul appel.
   *
   * Rend l'explication du refus quand il y en a un, le libellé normal sinon.
   * Évite le `:title="peut ? '…' : pourquoiPas('…')"` répété sur chaque bouton,
   * qui est exactement là où une incohérence se glisse.
   */
  const hint = (cle: string, normal: string): string => can(cle) ? normal : pourquoiPas(cle);

  return {
    can,
    pourquoiPas,
    hint,
    capabilitiesReady: computed(() => charge.value),
    loadCapabilities,
  };
}
